import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import invariant from 'tiny-invariant';
import { useSelector } from 'react-redux';

import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import { Card } from './Card';
import ColorWrapper from '@styles/colors';
import { ColumnContext, type ColumnContextProps } from './column-context';
import NoMachineColumnFilter from '@v2/pages/planboard/components/NoMachineColumnFilter';
import MachineChart from './MachineChart';
import Loading from '@components/loading';
import { TourTarget } from '@components/tours';

import { useBoardContext } from './Board/context';
import { scale } from '@components/frame';
import useColumnFetch from '@v2/pages/planboard/hooks/useColumnFetch';

import type { ColumnType, State } from './types';
import { ColorFunction } from '@styles/colors/types';
import { font } from '@styles/fonts';
import { selectPlanboardMachineColumn, selectReschedulingMachineIds, selectReschedulingFromPosition } from '@reducers/v2/planboard';
import { PLANBOARD_TILE_CONFIG } from '@v2/pages/common/constants/base.constants';

const idle: State = { type: 'idle' };
const isCardOver: State = { type: 'is-card-over' };

const ColumnComponent = ({ column, C }: { column: ColumnType; C: ColorFunction }) => {
  const columnId = column.columnId;
  const columnPadding = `${scale(PLANBOARD_TILE_CONFIG.COLUMN_PADDING_HORIZONTAL)}px`;
  const columnWidth = `${scale(PLANBOARD_TILE_CONFIG.COLUMN_CONTENT_WIDTH + PLANBOARD_TILE_CONFIG.COLUMN_PADDING_HORIZONTAL * 2)}px`;
  const columnRef = useRef<HTMLDivElement | null>(null);
  const columnInnerRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<State>(idle);
  const columnRecord = useSelector(selectPlanboardMachineColumn(columnId));
  const reschedulingMachineIds = useSelector(selectReschedulingMachineIds);
  const isColumnRescheduling = reschedulingMachineIds.includes(columnId);
  const reschedulingFromPosition = useSelector(selectReschedulingFromPosition);
  const reschedulingEntries = reschedulingFromPosition[columnId];
  const minReschedulingIndex = reschedulingEntries?.length
    ? Math.min(...reschedulingEntries.map((e) => e.fromIndex))
    : undefined;
  const [filter, setFilter] = useState('');

  const { observedEntryRef } = useColumnFetch(columnId, { total: columnRecord?.total, filter: filter.trim() });

  const { instanceId, registerColumn, pendingOperationIds, isScrolled } = useBoardContext();

  useEffect(() => {
    invariant(columnRef.current);
    invariant(columnInnerRef.current);
    return combine(
      registerColumn({
        columnId,
        entry: {
          element: columnRef.current,
        },
      }),
      dropTargetForElements({
        element: columnInnerRef.current,
        getData: () => ({ columnId }),
        canDrop: ({ source }) => {
          return source.data.instanceId === instanceId && source.data.type === 'card';
        },
        getIsSticky: () => true,
        onDragEnter: () => setState(isCardOver),
        onDragLeave: () => setState(idle),
        onDragStart: () => setState(isCardOver),
        onDrop: () => setState(idle),
      }),
      dropTargetForElements({
        element: columnRef.current,
        canDrop: ({ source }) => {
          return source.data.instanceId === instanceId && source.data.type === 'column';
        },
        getIsSticky: () => true,
        getData: ({ input, element, source }) => {
          const data = {
            columnId,
            element,
            source,
          };
          return attachClosestEdge(data, {
            input,
            element,
            allowedEdges: ['left', 'right'],
          });
        },
      })
    );
  }, [columnId, registerColumn, instanceId]);

  const stableItems = useRef(column.items);

  useEffect(() => {
    stableItems.current = column.items;
  }, [column.items]);

  const getCardIndex = useCallback((productionOperationId: number) => {
    return stableItems.current.findIndex((item) => item.productionOperationId === productionOperationId);
  }, []);

  const getNumCards = useCallback(() => {
    return stableItems.current.length;
  }, []);

  const getTotalCards = useCallback(() => {
    return columnRecord?.total ?? 0;
  }, [columnRecord?.total]);

  const contextValue: ColumnContextProps = useMemo(() => {
    return { columnId, getCardIndex, getNumCards, getTotalCards };
  }, [columnId, getCardIndex, getNumCards, getTotalCards]);

  return (
    <ColumnContext.Provider value={contextValue}>
      <div
        ref={columnRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: C('Gray/Light/6'),
          // Make "No Machine" column sticky on horizontal scroll with right shadow when scrolled
          position: columnId === -1 ? 'sticky' : 'relative',
          left: columnId === -1 ? 0 : undefined,
          zIndex: columnId === -1 ? 2 : undefined,
          boxShadow: columnId === -1 && isScrolled ? '4px 0 8px rgba(0, 0, 0, 0.08)' : undefined,
          transition: 'box-shadow 200ms ease-in-out, background-color 250ms ease-in-out',
          height: '100%',
          minWidth: columnWidth,
          maxWidth: columnWidth,
          boxSizing: 'border-box',
          ...{
            idle: {},
            'is-card-over': {
              backgroundColor: C('Gray/Light/5'),
            },
          }[state.type],
        }}
      >
        <TourTarget tourId={columnId === -1 ? 'planboard-no-machine-column' : 'planboard-machine-column'}>
          <h3
            style={{
              ...font['Headline Large'],
              lineHeight: '22px',
              fontFamily: '-apple-system, BlinkMacSystemFont, Roboto',
              margin: 0,
              padding: `${scale(12)}px 0px ${scale(9)}px ${scale(30)}px`,
              position: 'sticky',
              backgroundColor: state.type === 'idle' ? C('Gray/Light/6') : C('Gray/Light/5'),
              transition: `background-color 250ms ease-in-out`,
              top: `${scale(0)}px`,
              left: `${scale(12)}px`,
              zIndex: 1,
              color: C('Label/Light/Primary'),
            }}
          >
            {column.title}
          </h3>
        </TourTarget>
        {column.columnId === -1 ? (
          <NoMachineColumnFilter setFilter={setFilter} scale={scale} font={font} />
        ) : (
          <MachineChart font={font} scale={scale} />
        )}
        <div
          ref={columnInnerRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            boxSizing: 'border-box',
            minHeight: `${scale(250)}px`,
            gap: `${scale(4.5)}px`,
            paddingInline: columnPadding,
            height: '100%',
          }}
        >
          {column.items?.map((item, idx, arr) => {
            const observedEntryIdx = Math.floor(arr.length * 0.7);
            const isPending = isColumnRescheduling
              || pendingOperationIds.includes(item.productionOperationId)
              || (minReschedulingIndex !== undefined && idx >= minReschedulingIndex);
            return (
              <Card
                C={C}
                item={item}
                key={item.productionOperationId}
                isPending={isPending}
                ref={idx === observedEntryIdx ? observedEntryRef : null}
              />
            );
          })}
          {columnRecord?.loading ?? true ? (
            <div
              style={{
                maxHeight: `${scale(250)}px`,
                marginBlock: scale(12),
              }}
            >
              <Loading background={C('Gray/Light/6')} />
            </div>
          ) : null}
        </div>
      </div>
    </ColumnContext.Provider>
  );
};

export const Column = ColorWrapper(ColumnComponent);
